/**
 * The app's chrome.
 *
 * It lays out the working stage (DESIGN.md §2.2): the HUD across the top,
 * the Forge rail, the stage, the gates strip, the Inspector rail, the Wire
 * and the Director dock, with the Deck and the MCP lens as drawers and a tab
 * bar on a phone. Every region carries `data-region`, which is how F6 walks
 * them (ui/a11y.ts). Screens are mounted into the slots it hands out; the
 * shell itself shows only the HUD's readouts.
 *
 * Over everything sit the sheets: the confirm sheet (focus trap, and a
 * 600 ms hold for a pointer), the verb ring, the carried ghost, and the
 * capsule a secret is shown in once. A refusal's sentence and its fix-its are
 * the conductor's, drawn at the drop point in the Fx overlay.
 *
 * Also here, because main.ts merges them: the scene every screen's layouts
 * add up to, and the occupier hues it is drawn in. Everything a server sent
 * reaches the DOM as text.
 */

import type {
  AppContext, CarryState, Catalogue, FieldLayout, Fx, Ghost as FxGhost, Held, Json, LinkState, Operation, PlaneId, Persona, Route, Scene,
  Target, Unmount, Unsub, Verb, Viewer, WorldMatter,
} from '../contracts';
import { h, mount } from '../../../console/src/dom';
import { occupierHue } from '../fx/palette';
import { bindHold, focusablesIn, trapFocus } from './a11y';
import { isObj } from '../model/json';
import { badgeText, TRY_CHECK_HREF } from '../app/sandbox';

// ---------- preferences (theme, sound, motion) ----------

/** Dark is the hero and the default whatever the system prefers; light only when picked. */
export type ThemeChoice = 'dark' | 'light';
const THEME_KEY = 'schedule-bit.playground.theme';
const SOUND_KEY = 'schedule-bit.playground.sound';

/** The ◐ button: dark and light, in turn. */
export const nextTheme = (t: ThemeChoice): ThemeChoice => (t === 'dark' ? 'light' : 'dark');

const localStore = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};
const read = (key: string): string | null => {
  try { return localStore()?.getItem(key) ?? null; } catch { return null; }
};
const write = (key: string, value: string | null): void => {
  try {
    const s = localStore();
    if (!s) return;
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch { /* storage refused: the choice lasts for this page only */ }
};

/** What was remembered: light only if light was picked; anything else (nothing, an old 'system') is dark. */
export const themeChoiceOf = (raw: string | null): ThemeChoice => (raw === 'light' ? 'light' : 'dark');

/**
 * Theme, sound and reduced motion, shared by the HUD and the Connect screen.
 * Theme and sound persist in localStorage (§5.5 ViewState); reduced motion
 * starts from the system and lasts for the page.
 */
export class Prefs {
  private themeChoice: ThemeChoice = themeChoiceOf(read(THEME_KEY));
  private soundOn = read(SOUND_KEY) === 'on';
  private reducedOn = false;
  private fx: Fx | null = null;
  private readonly listeners = new Set<() => void>();

  bind(fx: Fx, reduced: boolean): void {
    this.fx = fx;
    this.reducedOn = reduced;
    fx.setSound(this.soundOn);
    fx.setReduced(reduced);
    this.applyTheme();
  }
  theme(): ThemeChoice { return this.themeChoice; }
  sound(): boolean { return this.soundOn; }
  reduced(): boolean { return this.reducedOn; }
  cycleTheme(): void {
    this.themeChoice = nextTheme(this.themeChoice);
    write(THEME_KEY, this.themeChoice === 'light' ? 'light' : null);
    this.applyTheme();
    this.tell();
  }
  setSound(on: boolean): void {
    this.soundOn = on;
    write(SOUND_KEY, on ? 'on' : null);
    this.fx?.setSound(on);
    this.tell();
  }
  setReduced(on: boolean): void {
    this.reducedOn = on;
    this.fx?.setReduced(on);
    if (typeof document !== 'undefined') document.documentElement.toggleAttribute('data-reduced', on);
    this.tell();
  }
  on(fn: () => void): Unsub {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }
  applyTheme(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (this.themeChoice === 'light') root.dataset.theme = 'light';
    else delete root.dataset.theme;
    root.toggleAttribute('data-reduced', this.reducedOn);
  }
  private tell(): void {
    for (const fn of [...this.listeners]) fn();
  }
}

/** One Prefs per page. */
export const prefs = new Prefs();

/** The ≈ ♪ ◐ buttons, kept in step with Prefs wherever they are shown. */
export const prefToggles = (): { el: HTMLElement; unmount: Unmount } => {
  const motion = h('button', { type: 'button', class: 'tog', onclick: () => prefs.setReduced(!prefs.reduced()) }, '≈');
  const sound = h('button', { type: 'button', class: 'tog', onclick: () => prefs.setSound(!prefs.sound()) }, '♪');
  const theme = h('button', { type: 'button', class: 'tog', onclick: () => prefs.cycleTheme() }, '◐');
  const paint = (): void => {
    motion.setAttribute('aria-pressed', String(prefs.reduced()));
    motion.setAttribute('aria-label', `Reduced motion ${prefs.reduced() ? 'on' : 'off'}`);
    motion.title = 'Reduced motion (≈)';
    sound.setAttribute('aria-pressed', String(prefs.sound()));
    sound.setAttribute('aria-label', `Sound ${prefs.sound() ? 'on' : 'off'}`);
    sound.title = 'Synthesized sound, off by default';
    theme.setAttribute('aria-label', `Theme: ${prefs.theme()}; next ${nextTheme(prefs.theme())}`);
    theme.title = `Theme: ${prefs.theme()}`;
    theme.dataset.theme = prefs.theme();
  };
  paint();
  const off = prefs.on(paint);
  return { el: h('span', { class: 'toggles' }, motion, sound, theme), unmount: off };
};

// ---------- readouts ----------

/** 312 → "312", 1500 → "1.5k", 100000 → "100k", 2000000 → "2M". */
export const compact = (n: number): string => {
  const a = Math.abs(n);
  const trim = (x: number): string => (Math.round(x * 10) / 10).toString();
  if (a < 1000) return String(Math.round(n));
  if (a < 1_000_000) return `${trim(n / 1000)}k`;
  return `${trim(n / 1_000_000)}M`;
};

/** A meter in `n` bars: "▮▮▯". Any use lights at least one. */
export const meterBars = (used: number, limit: number, n = 3): string => {
  const filled = limit > 0 ? Math.max(used > 0 ? 1 : 0, Math.min(n, Math.round((used / limit) * n))) : 0;
  return '▮'.repeat(filled) + '▯'.repeat(n - filled);
};

/** Whether an answered operation counts against the month's writes (core/api.ts's flag). */
export const isMeteredWrite = (op: Operation | undefined): boolean =>
  !!op && op.method !== 'GET' && op.security === 'key' && !op.path.includes('/watch-tickets')
  && !op.path.startsWith('/v1/billing') && !op.path.startsWith('/v1/assist');

export interface Person {
  readonly id: string;
  readonly name: string;
  readonly keyTail: string;
  readonly color: string;
  readonly tabs: number;
  readonly worlds: readonly string[];
}

/**
 * The HUD's roster: one chip per person (name + keyTail), never per socket.
 * A tab linked to several Worlds is on each of their rosters, so tabs are the
 * most seen on any one World, not the sum. This tab's own sockets are left out.
 */
export const groupPeople = (
  entries: readonly { world: string; viewer: Viewer; tabs: number }[],
  own: ReadonlySet<string>,
): Person[] => {
  const by = new Map<string, { name: string; keyTail: string; color: string; tabs: number; worlds: Set<string> }>();
  for (const e of entries) {
    if (own.has(e.viewer.sid)) continue;
    const id = `${e.viewer.name}|${e.viewer.keyTail}`;
    const p = by.get(id) ?? { name: e.viewer.name, keyTail: e.viewer.keyTail, color: e.viewer.color, tabs: 0, worlds: new Set<string>() };
    p.tabs = Math.max(p.tabs, e.tabs);
    p.worlds.add(e.world);
    by.set(id, p);
  }
  return [...by.entries()]
    .map(([id, p]) => ({ id, name: p.name, keyTail: p.keyTail, color: p.color, tabs: p.tabs, worlds: [...p.worlds].sort() }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.keyTail.localeCompare(b.keyTail));
};

export const personLabel = (p: Person): string => (p.tabs > 1 ? `${p.name} ×${p.tabs}` : p.name);

/** The links wire: live when all are, retrying when any is, else polling or down. */
export const linksSummary = (states: readonly LinkState[]): { live: number; state: LinkState | 'none' } => {
  const live = states.filter((s) => s === 'live').length;
  if (states.length === 0) return { live, state: 'none' };
  const state: LinkState = states.includes('retrying') || states.includes('connecting') ? 'retrying'
    : states.includes('polling') ? 'polling' : live > 0 ? 'live' : 'closed';
  return { live, state };
};

/** A workspace's 3×3 sigil, mirrored left to right: nine bits from its name. */
export const sigilBits = (name: string): boolean[] => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i += 1) hash = Math.imul(hash ^ name.charCodeAt(i), 0x01000193) >>> 0;
  const cells: boolean[] = [];
  for (let row = 0; row < 3; row += 1) {
    const left = ((hash >>> (row * 2)) & 1) === 1;
    const mid = ((hash >>> (row * 2 + 1)) & 1) === 1;
    cells.push(left, mid, left);
  }
  if (!cells.some(Boolean)) cells[4] = true;
  return cells;
};

// ---------- hues and the scene ----------

/**
 * §4.1: occupier i's hue is the i-th of (178 + 137.508·n) mod 360 that avoids the refuse (15–40°),
 * ask (60–95°) and mine/door (165–195°) bands: the palette's, one walk for the rings, the field and the GL bits.
 */
export { occupierHue };

export const occupierIdOf = (plane: PlaneId): number => Number(plane.split('|')[0]);

export interface SceneParts {
  readonly layouts: ReadonlyMap<string, readonly FieldLayout[]>;
  readonly selection: Scene['selection'];
  readonly hints: Scene['hints'];
  readonly matter: (world: string) => WorldMatter | undefined;
  readonly pinned?: (world: string, occupierId: number) => boolean;
  readonly occupierId?: (world: string, name: string) => number | null;
}

/** Every owner's layouts in publish order, one scene for the Fx. */
export const buildScene = (parts: SceneParts): Scene => ({
  layouts: [...parts.layouts.values()].flat(),
  matter: parts.matter,
  hue: (_world, plane) => occupierHue(occupierIdOf(plane)),
  selection: parts.selection,
  hints: parts.hints,
  ...(parts.pinned ? { pinned: parts.pinned } : {}),
  ...(parts.occupierId ? { occupierId: parts.occupierId } : {}),
});

const hintSlots = (v: unknown): bigint | null => {
  if (typeof v === 'string' && /^0x[0-9a-f]+$/i.test(v)) return BigInt(v);
  if (typeof v === 'number' && Number.isInteger(v) && v >= 0) return 1n << BigInt(v);
  if (Array.isArray(v) && v.every((x) => typeof x === 'number')) return (v as number[]).reduce((m, x) => m | (1n << BigInt(x)), 0n);
  return null;
};

/** The dashed preview under a carry: the target's bits, with the local verdict. */
export const hintsOf = (state: CarryState): Scene['hints'] => {
  const over = state.over;
  if (!over || !over.world || !state.verdict || typeof over.data !== 'object' || over.data === null || Array.isArray(over.data)) return [];
  const data = over.data as Record<string, unknown>;
  const key = typeof data.key === 'number' ? data.key : null;
  const slots = hintSlots(data.slots ?? data.slot);
  return key === null || slots === null ? [] : [{ world: over.world, key, slots, verdict: state.verdict }];
};

// ---------- the layout ----------

export type Mode = 'boot' | 'connect' | 'app';
export type Sheet = 'forge' | 'wire' | 'more' | null;

export interface StageSlots {
  readonly stage: HTMLElement;
  readonly field?: HTMLElement;
  readonly river?: HTMLElement;
  readonly roster?: HTMLElement;
}

export interface ShellOptions {
  readonly root: HTMLElement;
  readonly sheets: HTMLElement;
  readonly fx: Fx;
}

export interface Shell {
  readonly hud: HTMLElement;
  readonly forge: HTMLElement;
  readonly gates: HTMLElement;
  readonly inspector: HTMLElement;
  readonly wire: HTMLElement;
  readonly director: HTMLElement;
  readonly deck: HTMLElement;
  readonly mcp: HTMLElement;
  readonly tabs: HTMLElement;
  setMode(mode: Mode): void;
  /** Clear the stage and lay out what a route's screens mount into. */
  stageFor(route: Route): StageSlots;
  drawer(which: 'deck' | 'mcp' | null): void;
  toggleDrawer(which: 'deck' | 'mcp'): void;
  sheet(which: Sheet): void;
  /** Slide the Inspector over the stage, or away ('toggle' flips it); it never covers the stage uninvited. */
  inspect(open: boolean | 'toggle'): void;
  inspecting(): boolean;
  /** Put away the top-most thing that floats over the stage (Esc): a drawer, the Inspector, the Wire's log, a sheet. True if one went. */
  dismiss(): boolean;
  confirm(req: { title: string; body: string; kind: 'destructive' | 'money' | 'server'; confirmLabel: string }): Promise<boolean>;
  ghost(held: Held | null, at?: { x: number; y: number }, home?: boolean): void;
  /** Show the verb ring; the returned function closes it without telling `onClose`. */
  ring(verbs: readonly Verb[], opts: RingOptions): () => void;
  capsule(opts: CapsuleOptions): void;
  notice(text: string, tone?: 'ok' | 'warn' | 'refuse'): void;
  splash(text: string): void;
}

/**
 * The composition: the stage is full-bleed and the GL layer under it is the
 * hero; everything else floats over it as dark glass. The HUD across the
 * top, the tool rail (Forge, then the gates) down the left edge, the roster
 * beside it in a World, the Inspector sliding over from the right on
 * demand, the Wire along the bottom edge as a one-line ticker that opens
 * into the log, and the Director as a command bar above it.
 */
export const createShell = (opts: ShellOptions): Shell => {
  const { root, sheets, fx } = opts;
  const hud = h('header', { class: 'hud', 'aria-label': 'Status' });
  const forgeTools = h('div', { class: 'forge-tools' });
  const forge = h('aside', { class: 'rail forge', 'data-region': 'forge', 'aria-label': 'Forge' }, forgeTools);
  const gates = h('div', { class: 'rail gates', 'data-region': 'gates', 'aria-label': 'Gates' });
  const roster = h('aside', { class: 'roster-dock', 'data-region': 'roster', 'aria-label': 'Roster', hidden: true });
  const stage = h('main', { class: 'stage', id: 'stage', 'data-region': 'stage', 'aria-label': 'Stage' });
  const inspector = h('div', { class: 'insp-slot' });
  const inspX = h('button', { type: 'button', class: 'panel-x', 'aria-label': 'Close the Inspector (Esc)', title: 'Close (Esc)' }, '×');
  const inspPanel = h('aside', { class: 'insp-panel', 'data-region': 'inspector', 'aria-label': 'Inspector' }, inspX, inspector);
  const wire = h('section', { class: 'wire-dock', 'data-region': 'wire', 'aria-label': 'Wire' });
  const director = h('section', { class: 'dock', 'data-region': 'director', 'aria-label': 'Director' });
  const deck = h('section', { class: 'drawer deck', 'data-region': 'deck', 'aria-label': 'Deck', hidden: true });
  const mcp = h('section', { class: 'drawer mcp', 'data-region': 'mcp', 'aria-label': 'MCP lens', hidden: true });
  const tabs = h('nav', { class: 'tabs', 'aria-label': 'Panels' });
  const app = h('div', { class: 'app', 'data-mode': 'boot' },
    h('div', { class: 'frame' }, stage),
    hud, h('div', { class: 'toolrail' }, forge, gates), roster, inspPanel, wire, director, deck, mcp, tabs);
  mount(root, app);

  let ghost: Ghost | null = null;
  const ghostOf = (): Ghost => (ghost ??= createGhost(sheets, fx));
  let confirmChain: Promise<unknown> = Promise.resolve();

  // The Inspector: inert while away, so Tab and F6 pass it by; focus that was in it goes back where it came from.
  let inspFrom: HTMLElement | null = null;
  let drawerFrom: HTMLElement | null = null;
  const setInspector = (open: boolean): void => {
    const was = app.dataset.insp === 'open';
    app.dataset.insp = open ? 'open' : '';
    inspPanel.inert = !open;
    if (open && !was) inspFrom = document.activeElement instanceof HTMLElement && !inspPanel.contains(document.activeElement) ? document.activeElement : null;
    if (!open && was) {
      if (inspPanel.contains(document.activeElement)) (inspFrom?.isConnected ? inspFrom : stage).focus?.();
      inspFrom = null;
    }
  };
  setInspector(false);
  inspX.addEventListener('click', () => setInspector(false));
  inspPanel.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    e.preventDefault();
    setInspector(false);
  });

  const shell: Shell = {
    hud, forge: forgeTools, gates, inspector, wire, director, deck, mcp, tabs,
    setMode(mode) {
      app.dataset.mode = mode;
      if (mode !== 'app') { shell.drawer(null); setInspector(false); }
    },
    stageFor(route) {
      app.dataset.screen = route.name;
      stage.replaceChildren();
      const worldy = route.name === 'world' || route.name === 'relay' || route.name === 'inbox';
      roster.hidden = !worldy;
      roster.replaceChildren();
      if (route.name === 'world') {
        const field = h('div', { class: 'field-host', 'data-region': 'field', 'aria-label': `Field of ${route.world}` });
        const river = h('div', { class: 'river-host', 'data-region': 'river', 'aria-label': 'River' });
        stage.append(h('div', { class: 'world-view' }, field, river));
        return { stage, field, river, roster };
      }
      return worldy ? { stage, roster } : { stage };
    },
    drawer(which) {
      const active = document.activeElement as HTMLElement | null;
      const was = !deck.hidden ? deck : !mcp.hidden ? mcp : null;
      // Where focus was before a drawer opened: it goes back there when the drawer closes around it.
      if (!was && which && active && active !== document.body) drawerFrom = active;
      const heldFocus = !!was && !!active && was.contains(active);
      deck.hidden = which !== 'deck';
      mcp.hidden = which !== 'mcp';
      app.dataset.drawer = which ?? '';
      const open = which === 'deck' ? deck : which === 'mcp' ? mcp : null;
      if (open) (open.querySelector<HTMLElement>('[data-region-focus]') ?? focusablesIn(open)[0] ?? open).focus?.();
      else if (heldFocus || document.activeElement === document.body) {
        const back = drawerFrom?.isConnected ? drawerFrom : null;
        drawerFrom = null;
        back?.focus();
      }
    },
    toggleDrawer(which) {
      shell.drawer((which === 'deck' ? !deck.hidden : !mcp.hidden) ? null : which);
    },
    sheet(which) {
      app.dataset.sheet = which ?? '';
      if (which === 'more') setInspector(true);
    },
    inspect(open) {
      setInspector(open === 'toggle' ? app.dataset.insp !== 'open' : open);
    },
    inspecting: () => app.dataset.insp === 'open',
    dismiss() {
      if (!deck.hidden || !mcp.hidden) { shell.drawer(null); return true; }
      if (app.dataset.insp === 'open') { setInspector(false); return true; }
      const wireOpen = wire.querySelector<HTMLButtonElement>('.wire[data-open] > .wire-ticker');
      if (wireOpen) { wireOpen.click(); return true; }
      if (app.dataset.sheet) { shell.sheet(null); return true; }
      return false;
    },
    confirm(req) {
      const next = confirmChain.then(() => openConfirm(sheets, req));
      confirmChain = next.catch(() => undefined);
      return next;
    },
    ghost: (held, at, home) => ghostOf().show(held, at, home),
    ring: (verbs, o) => showRing(sheets, verbs, o),
    capsule: (o) => showCapsule(sheets, o),
    notice: (text, tone = 'ok') => showNotice(sheets, text, tone),
    splash(text) {
      app.dataset.screen = 'boot';
      mount(stage, h('div', { class: 'splash', role: 'status' }, h('p', { class: 'eyebrow' }, 'SCHEDULE · BIT'), h('p', { class: 'splash-line' }, text)));
    },
  };
  return shell;
};

// ---------- the HUD ----------

export interface HudDeps {
  links(): readonly { world: string; state: LinkState }[];
  onLinks(fn: () => void): Unsub;
  disconnect(): void;
  openDrawer(which: 'deck' | 'mcp'): void;
  /** Stop and keep a recording under a title, or drop it (null). */
  stopRecording(): void;
  /** Open the ⌘K palette (the Deck's drawer with it). */
  palette(): void;
  /** Slide the Inspector over, or away (I). */
  inspector(): void;
  inspecting(): boolean;
  /** The sheet of what the words mean (?). */
  glossary(): void;
}

const text = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const count = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const sigil = (name: string): HTMLElement =>
  h('span', { class: 'sigil-bits', 'aria-hidden': 'true' }, ...sigilBits(name).map((on) => h('i', { class: on ? 'on' : '' })));

/** DESIGN.md §2.2's top bar. Reads records, the rate headers of answers, the links and the roster. */
export const mountHud = (el: HTMLElement, ctx: AppContext, deps: HudDeps): Unmount => {
  const offs: Unsub[] = [];
  let rate: { used: number; limit: number } | null = null;
  let localWrites = 0;
  let pulse: { ok: boolean; ms: number | null } = { ok: true, ms: null };

  const sigilBtn = h('button', { type: 'button', class: 'sigil', title: 'Workspace sigil: a plan tile lands here (checkout); carry it to the Portal gate' });
  const wsText = h('span', { class: 'ws' });
  const personaChip = (p: Persona): HTMLButtonElement => h('button', { type: 'button', class: 'p', title: `Lens here: who is key ${p} (whoami, as persona ${p})` }, p);
  const chips = { A: personaChip('A'), B: personaChip('B'), C: personaChip('C') } as const;
  const persona = h('span', { class: 'persona', role: 'group', 'aria-label': 'Personas' }, chips.A, chips.B, chips.C);
  const writes = h('button', { type: 'button', class: 'meter', title: 'Writes this month (Lens here: usage)' });
  const reqs = h('span', { class: 'meter rate', title: 'Requests this minute, from the ratelimit headers' });
  const links = h('span', { class: 'links', title: 'Live links to Worlds (watch sockets)' });
  const peers = h('span', { class: 'peers', 'aria-label': 'People here' });
  const health = h('button', { type: 'button', class: 'pulse', title: 'Pulse: Lens here (or Enter) to check health' });
  const rec = h('button', { type: 'button', class: 'rec', hidden: true, title: 'Stop recording and keep it as a use case' }, '● REC');
  const palette = h('button', { type: 'button', class: 'kbd', title: 'Palette (⌘K)', 'aria-label': 'Open the palette' }, '⌘K');
  const cases = h('button', { type: 'button', class: 'kbd cases', title: 'The Gallery: use cases the stage plays for you, no AI needed (G)' }, '▶ Cases');
  const help = h('button', { type: 'button', class: 'kbd help', title: 'What the words mean (?)', 'aria-label': 'What the words mean' }, '?');
  const brand = h('button', { type: 'button', class: 'brand', title: 'The stage: every World (Esc from a World)', 'aria-label': 'Back to the stage' }, 'S·B');
  const crumbs = h('nav', { class: 'crumbs', 'aria-label': 'Where you are' });
  const insp = h('button', { type: 'button', class: 'kbd insp-toggle', title: 'Inspector: the call, its REST, curl and MCP forms (I)', 'aria-label': 'Inspector', 'aria-pressed': 'false' }, '◧');
  const toggles = prefToggles();
  const menu = h('button', { type: 'button', class: 'menu', 'aria-label': 'More', 'aria-expanded': 'false' }, '≡');
  const leave = h('button', { type: 'button', class: 'leave', title: 'Forget this tab\'s keys and return to Connect' }, 'Disconnect');
  // A sandbox says how long it has left, and where a workspace of one's own comes from.
  const sandboxText = h('span', { class: 'sb-left' });
  const signUp = h('a', { class: 'sb-signup', href: TRY_CHECK_HREF.replace('#try=1', ''), target: '_blank', rel: 'noopener noreferrer' }, 'keep your work: sign up');
  const sandboxBadge = h('span', { class: 'sandbox-badge', role: 'status', hidden: true }, sandboxText, signUp);
  const extra = h('div', { class: 'hud-extra' }, writes, reqs, peers, health, rec, cases, help, palette, insp, toggles.el, leave);
  mount(el, h('div', { class: 'hud-row' }, brand, sigilBtn, wsText, sandboxBadge, crumbs, persona, links, extra, menu));
  const paintSandbox = (): void => {
    const box = ctx.session.sandbox();
    sandboxBadge.hidden = !box;
    if (box) sandboxText.textContent = badgeText(box.expiresAt, Date.now());
  };
  paintSandbox();
  const sandboxTimer = setInterval(paintSandbox, 30_000);
  offs.push(() => clearInterval(sandboxTimer), ctx.session.on(paintSandbox));
  brand.addEventListener('click', () => ctx.router.go({ name: 'stage' }));
  cases.addEventListener('click', () => ctx.router.go({ name: 'gallery' }));
  help.addEventListener('click', () => deps.glossary());
  /** Stage / World / relay: where the tab is, each step a way back. */
  const paintCrumbs = (): void => {
    const r = ctx.router.current();
    const step = (label: string, to: Route | null, title?: string): HTMLElement => (to
      ? h('a', { href: ctx.router.href(to), class: 'crumb', ...(title ? { title } : {}) }, label)
      : h('span', { class: 'crumb here', 'aria-current': 'page' }, label));
    const sep = (): HTMLElement => h('span', { class: 'sep', 'aria-hidden': 'true' }, '/');
    const parts: HTMLElement[] = [step('Stage', r.name === 'stage' ? null : { name: 'stage' }, 'Every World')];
    if (r.name === 'world') parts.push(sep(), step(r.world, null));
    if (r.name === 'relay' || r.name === 'inbox') parts.push(sep(), step(r.world, { name: 'world', world: r.world }), sep(), step(r.name === 'relay' ? `⟿ ${r.relay}` : 'inbox', null));
    if (r.name === 'gallery') parts.push(sep(), step('Cases', null));
    if (r.name === 'account') parts.push(sep(), step('Account', null));
    mount(crumbs, ...parts);
    cases.setAttribute('aria-pressed', String(r.name === 'gallery'));
  };
  offs.push(ctx.router.on(paintCrumbs));
  paintCrumbs();
  insp.addEventListener('click', () => { deps.inspector(); insp.setAttribute('aria-pressed', String(deps.inspecting())); });

  menu.addEventListener('click', () => {
    const open = !el.classList.contains('is-open');
    el.classList.toggle('is-open', open);
    menu.setAttribute('aria-expanded', String(open));
  });
  palette.addEventListener('click', () => deps.palette());
  leave.addEventListener('click', () => deps.disconnect());
  rec.addEventListener('click', () => deps.stopRecording());
  health.addEventListener('click', () => { void ctx.actions.run('health', {}, { label: 'Pulse', source: { kind: 'hand', via: 'click' } }).then(fromHealth); });

  const fromHealth = (record: { answer: { status: number; ms: number } | null } | null): void => {
    if (!record?.answer) return;
    pulse = { ok: record.answer.status > 0 && record.answer.status < 500, ms: record.answer.ms };
    paint();
  };

  const me = (): { workspace: string | null; plan: string | null; keyTail: string | null } => {
    const m = ctx.records.get(null, 'me');
    if (!isObj(m)) return { workspace: null, plan: null, keyTail: null };
    const key = isObj(m.key) ? text(m.key.id) : null;
    return {
      workspace: text(m.workspace),
      plan: isObj(m.plan) ? text(m.plan.name) ?? text(m.plan.id) : null,
      keyTail: key ? key.slice(-4) : null,
    };
  };

  const paint = (): void => {
    const who = me();
    mount(sigilBtn, sigil(who.workspace ?? '?'));
    sigilBtn.setAttribute('aria-label', `Workspace ${who.workspace ?? 'unknown'}`);
    mount(wsText, h('strong', {}, who.workspace ?? '…'), who.plan ? h('span', { class: 'muted' }, ` · ${who.plan}`) : null);

    const people = ctx.session.personas();
    for (const p of ['A', 'B', 'C'] as const) {
      chips[p].classList.toggle('on', people.includes(p));
      chips[p].setAttribute('aria-label', `Persona ${p}${people.includes(p) ? '' : ' (no key)'}: drop the Lens here for whoami`);
    }

    const usage = ctx.records.get(null, 'usage');
    const used = isObj(usage) && isObj(usage.usage) ? count(usage.usage.writes) : null;
    const limit = isObj(usage) && isObj(usage.plan) && isObj(usage.plan.limits) ? count(usage.plan.limits.monthlyWrites) : null;
    if (used !== null && limit !== null) {
      const now = used + localWrites;
      mount(writes, h('span', { class: 'lab' }, 'writes'), h('span', { class: 'bars' }, meterBars(now, limit)), h('span', { class: 'num' }, `${compact(now)}/${compact(limit)}`));
      writes.setAttribute('aria-label', `Writes this month: ${now} of ${limit}`);
    } else {
      mount(writes, h('span', { class: 'lab' }, 'writes'), h('span', { class: 'num' }, '—'));
      writes.setAttribute('aria-label', 'Writes this month: not known');
    }
    if (rate) {
      mount(reqs, h('span', { class: 'lab' }, 'req/min'), h('span', { class: 'bars' }, meterBars(rate.used, rate.limit, 2)), h('span', { class: 'num' }, `${rate.used}/${rate.limit}`));
    } else mount(reqs, h('span', { class: 'lab' }, 'req/min'), h('span', { class: 'num' }, '—'));

    const summary = linksSummary(deps.links().map((l) => l.state));
    links.dataset.state = summary.state;
    mount(links, h('i', { class: 'dot', 'aria-hidden': 'true' }), ` live ${summary.live}`);
    links.setAttribute('aria-label', `${summary.live} live links${summary.state === 'retrying' ? ', some reconnecting' : summary.state === 'polling' ? ', some polling' : ''}`);

    const own = new Set<string>();
    for (const e of ctx.roster.all()) {
      const you = ctx.roster.you(e.world);
      if (you) own.add(you.sid);
    }
    const people2 = groupPeople(ctx.roster.all(), own);
    mount(peers, ...people2.slice(0, 6).map((p) => {
      const chip = h('button', {
        type: 'button', class: 'peer',
        title: `${p.name} · key …${p.keyTail} · on ${p.worlds.join(', ')}${p.tabs > 1 ? ` · ${p.tabs} tabs` : ''} (follow)`,
      }, h('i', { class: 'ring', 'aria-hidden': 'true' }), personLabel(p));
      chip.style.setProperty('--peer', p.color);
      chip.addEventListener('click', () => { if (p.worlds[0]) ctx.router.go({ name: 'world', world: p.worlds[0] }); });
      return chip;
    }), people2.length > 6 ? h('span', { class: 'more' }, `+${people2.length - 6}`) : null);

    health.dataset.state = pulse.ok ? 'up' : 'down';
    mount(health, h('i', { class: 'dot', 'aria-hidden': 'true' }), pulse.ok ? (pulse.ms === null ? ' API' : ` ${Math.round(pulse.ms)} ms`) : ' API down');
    health.setAttribute('aria-label', pulse.ok ? `API up${pulse.ms === null ? '' : `, ${Math.round(pulse.ms)} ms`}` : 'API unreachable');

    rec.hidden = !ctx.recorder.recording();
    insp.setAttribute('aria-pressed', String(deps.inspecting()));
    document.documentElement.toggleAttribute('data-operator', ctx.session.operatorKey() !== null);
  };

  const target = (el2: HTMLElement, t: Target): void => { offs.push(ctx.gestures.target(el2, () => t, 'stage')); };
  target(health, { kind: 'pulse', id: 'pulse', data: null });
  target(writes, { kind: 'meter', id: 'meter', data: null });
  for (const p of ['A', 'B', 'C'] as const) target(chips[p], { kind: 'persona', id: `persona:${p}`, persona: p, data: { persona: p } });
  target(sigilBtn, { kind: 'sigil', id: 'sigil', data: null });
  offs.push(ctx.gestures.source(sigilBtn, () => ({ kind: 'sigil', id: 'sigil', label: `workspace ${me().workspace ?? ''}`.trim(), data: { workspace: me().workspace } }), 'stage'));

  offs.push(ctx.records.on((_w, kind) => { if (kind === 'me' || kind === 'usage') { if (kind === 'usage') localWrites = 0; paint(); } }));
  offs.push(ctx.roster.on(() => paint()));
  offs.push(deps.onLinks(() => paint()));
  offs.push(ctx.session.on(() => paint()));
  offs.push(ctx.client.on((record, phase) => {
    if (phase !== 'answered' || !record.answer) return;
    const r = record.answer.rate;
    if (r && record.persona === 'A') rate = { used: Math.max(0, r.limit - r.remaining), limit: r.limit };
    if (record.persona === 'A' && record.answer.status > 0 && record.answer.status !== 401 && record.answer.status !== 429
      && isMeteredWrite(ctx.catalogue().byId(record.op))) localWrites += 1;
    if (record.op === 'health') fromHealth(record);
    paint();
  }));

  const check = (): void => {
    void ctx.client.call('health', {}, { source: { kind: 'auto' } }).then(fromHealth);
  };
  const minute = setInterval(() => { if (!pulse.ok && !document.hidden) check(); }, 60_000);
  const usageEvery = setInterval(() => { if (!document.hidden) void ctx.records.refresh(null, 'usage'); }, 5 * 60_000);
  const opWatch = setInterval(paint, 5_000);
  check();
  void ctx.records.refresh(null, 'me');
  void ctx.records.refresh(null, 'usage');
  paint();

  return () => {
    clearInterval(minute);
    clearInterval(usageEvery);
    clearInterval(opWatch);
    toggles.unmount();
    for (const off of offs) off();
    el.replaceChildren();
  };
};

// ---------- the Forge rail and the gates strip ----------

/** A tool on the rail: what it is for (`hint`, in words), and the gesture and operation it makes (`op`). */
interface ForgeItem { readonly glyph: string; readonly label: string; readonly hint: string; readonly op: string; readonly held: Held }

/** Things with no World of their own to hold them: seeds, and blanks for account objects. */
export const FORGE_ITEMS: readonly ForgeItem[] = [
  { glyph: '◎', label: 'hour', hint: 'A new World of hours (a room, a doctor): drop it on the stage', op: 'seed → ground · createWorld', held: { kind: 'seed', id: 'seed:temporal-hour', label: 'hour World', data: { preset: 'temporal-hour' } } },
  { glyph: '◎', label: 'minute', hint: 'A new World of minutes (an oven, a bay): drop it on the stage', op: 'seed → ground · createWorld', held: { kind: 'seed', id: 'seed:temporal-minute', label: 'minute World', data: { preset: 'temporal-minute' } } },
  { glyph: '▦', label: 'seat', hint: 'One more seat: two can hold the same hour', op: 'seat → World header · setCapacity', held: { kind: 'seat', id: 'seat', label: 'one more seat', data: { seats: 1 } } },
  { glyph: '⟿', label: 'relay', hint: 'Plan one job through several Worlds: drop it on the World that plans it', op: 'spine → ring · layRelay', held: { kind: 'spine', id: 'spine', label: 'relay spine', data: null } },
  { glyph: '⌇', label: 'bridge', hint: 'Let another workspace see a World: drop it on that World', op: 'bridge → ring · giveGrant', held: { kind: 'bridge', id: 'bridge:new', label: 'new bridge', data: { can: ['see'] } } },
  { glyph: '⚿', label: 'key', hint: 'A new API key: drop it on the Vault (Account)', op: 'key → vault · createKey', held: { kind: 'blankKey', id: 'key:new', label: 'new key', data: { scopes: ['read'] } } },
  { glyph: '⌁', label: 'antenna', hint: 'A webhook that posts events to a URL: drop it on the rack (Account)', op: 'antenna → rack · createWebhook', held: { kind: 'antenna', id: 'antenna:new', label: 'new webhook', data: { events: ['*'] } } },
];

/** A rail tool's card: its name, what it is for, then the gesture and operation, sliding out beside it on hover and focus. */
const tip = (label: string, hint: string, op?: string): HTMLElement =>
  h('span', { class: 'tip', 'aria-hidden': 'true' }, label, h('span', { class: 'tip-means' }, hint), op ? h('code', {}, op) : null);

/** A seed for a preset the API offers (§3.2 row 2: the presets answer rebuilds the seeds). */
export const seedItem = (preset: string): ForgeItem => {
  const short = preset.replace(/^temporal-/, '');
  return { glyph: '◎', label: short, hint: `A new World (${short}): drop it on the stage`, op: `seed → ground · createWorld (${preset})`, held: { kind: 'seed', id: `seed:${preset}`, label: `${short} World`, data: { preset } } };
};

/** The presets a `presets` answer lists, by id. */
export const presetsOf = (body: Json | undefined): string[] => {
  const list = isObj(body) && Array.isArray(body.presets) ? body.presets : [];
  return list.flatMap((p) => (isObj(p) && typeof p.id === 'string' ? [p.id] : []));
};

export const mountForge = (el: HTMLElement, ctx: AppContext): Unmount => {
  const offs: Unsub[] = [];
  let seedOffs: Unsub[] = [];
  const button = (item: ForgeItem, into: Unsub[]): HTMLButtonElement => {
    const b = h('button', { type: 'button', class: 'forge-item', 'aria-label': `${item.held.label}: ${item.hint} (${item.op})` },
      h('span', { class: 'glyph', 'aria-hidden': 'true' }, item.glyph), tip(item.held.label, item.hint, item.op));
    // The card goes when the carry starts or ends: it never sits over the stage after a drag.
    b.addEventListener('pointerup', () => b.blur());
    into.push(ctx.gestures.source(b, () => item.held, 'forge'));
    return b;
  };
  // The seed tray: the Lens carried here reads the presets, and their answer rebuilds the seeds.
  const tray = h('div', { class: 'forge-seeds', role: 'group', tabindex: '-1', 'aria-label': 'Seed tray: carry the Lens here to read the presets' });
  const seeds = (items: readonly ForgeItem[]): void => {
    for (const off of seedOffs) off();
    seedOffs = [];
    mount(tray, ...items.map((item) => button(item, seedOffs)));
  };
  seeds(FORGE_ITEMS.filter((i) => i.held.kind === 'seed'));
  offs.push(ctx.gestures.target(tray, () => ({ kind: 'tray', id: 'tray:seeds', data: { tray: 'seeds', label: 'the seed tray' } }), 'forge'));
  offs.push(ctx.client.on((record, phase) => {
    if (phase !== 'answered' || record.op !== 'presets' || !record.answer || record.answer.status >= 300) return;
    const ids = presetsOf(record.answer.body);
    if (ids.length > 0) seeds(ids.map(seedItem));
  }));
  const rest = FORGE_ITEMS.filter((i) => i.held.kind !== 'seed').map((item) => button(item, offs));
  mount(el, h('div', { class: 'forge-list', role: 'group', 'aria-label': 'Forge' }, tray, ...rest));
  return () => { for (const off of [...offs, ...seedOffs]) off(); el.replaceChildren(); };
};

export interface GateDeps {
  openDrawer(which: 'deck' | 'mcp'): void;
}

/** DESIGN.md §2.2's strip under the stage: LENS, VOID, DOOR, DECK, MCP, TAP, NEEDLE. */
export const mountGates = (el: HTMLElement, ctx: AppContext, deps: GateDeps): Unmount => {
  const offs: Unsub[] = [];
  const tool = (glyph: string, label: string, hint: string, op?: string): HTMLButtonElement =>
    h('button', { type: 'button', class: 'gate', 'aria-label': `${label}: ${hint}${op ? ` (${op})` : ''}` },
      h('span', { class: 'glyph', 'aria-hidden': 'true' }, glyph), tip(label, hint, op));
  const lens = tool('⌕', 'LENS', 'Read anything: carry it onto what you want read', 'lens → anything · the read it names');
  const voidGate = tool('⊘', 'VOID', 'Let go: drop a booking, an ask or a key here to undo it', 'release · giveUp · withdraw · revoke · delete');
  const door = tool('◎', 'DOOR', 'Ask the door whether a booking would go through, without making it', 'person → door · explainClaim');
  const deckBtn = tool('▤', 'DECK', 'Every operation of the API as a card you can fill and fire (⌘K)');
  const mcpBtn = tool('◇', 'MCP', 'The same operations as MCP tools: list and call them (M)', 'initialize · tools/list · tools/call');
  const tap = tool('⌁', 'TAP', 'Watch a World live: drop it on a World ring', 'tap → ring · watchTicket, then watch');
  const needle = tool('⧗', 'NEEDLE', 'Sweep away soft holds whose time ran out', 'needle → field · sweep');
  voidGate.classList.add('void');
  door.classList.add('door');
  const deckCount = h('span', { class: 'count' });
  const mcpCount = h('span', { class: 'count' });
  deckBtn.append(deckCount);
  mcpBtn.append(mcpCount);
  const counts = (c: Catalogue): void => {
    deckCount.textContent = String(c.ops.length);
    mcpCount.textContent = String(c.ops.filter((o) => o.mcp).length);
  };
  counts(ctx.catalogue());
  offs.push(ctx.spec.on(counts));
  deckBtn.addEventListener('click', () => deps.openDrawer('deck'));
  mcpBtn.addEventListener('click', () => deps.openDrawer('mcp'));

  const focusWorld = (): string | undefined => ctx.focus().world;
  offs.push(ctx.gestures.source(lens, () => ({ kind: 'lens', id: 'lens', label: 'Lens', data: null }), 'gates'));
  offs.push(ctx.gestures.source(tap, () => ({ kind: 'tap', id: 'tap', label: 'Tap', data: null }), 'gates'));
  offs.push(ctx.gestures.source(needle, () => ({ kind: 'needle', id: 'needle', label: 'Needle', ...(focusWorld() ? { world: focusWorld()! } : {}), data: null }), 'gates'));
  const gate = (b: HTMLElement, kind: Target['kind'], id: string): void => {
    offs.push(ctx.gestures.target(b, () => {
      const w = focusWorld();
      return { kind, id, ...(w ? { world: w } : {}), data: null };
    }, 'gates'));
    offs.push(ctx.fx.origins.register(id, () => centerOf(b)));
  };
  gate(voidGate, 'gate:void', 'gate:void');
  gate(door, 'gate:door', 'gate:door');
  gate(mcpBtn, 'gate:mcp', 'gate:mcp');
  gate(deckBtn, 'deck', 'deck');
  mount(el, h('div', { class: 'gate-row', role: 'group', 'aria-label': 'Gates and tools' }, lens, voidGate, door, deckBtn, mcpBtn, tap, needle));
  return () => { for (const off of offs) off(); el.replaceChildren(); };
};

export const centerOf = (el: Element): { x: number; y: number } | null => {
  const r = el.getBoundingClientRect();
  return r.width === 0 && r.height === 0 ? null : { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

/** The phone's tab bar: [Forge] [Deck] [Cases] [Wire] [⋯]. */
export const mountTabs = (el: HTMLElement, ctx: AppContext, shell: Shell): Unmount => {
  const tab = (label: string, on: () => void): HTMLButtonElement => h('button', { type: 'button', class: 'tab', onclick: on }, label);
  let open: Sheet = null;
  const flip = (s: Exclude<Sheet, null>): void => { open = open === s ? null : s; shell.sheet(open); };
  mount(el,
    tab('Forge', () => flip('forge')),
    tab('Deck', () => shell.toggleDrawer('deck')),
    tab('Cases', () => ctx.router.go({ name: 'gallery' })),
    tab('Wire', () => flip('wire')),
    tab('⋯', () => { open = null; shell.sheet(null); shell.inspect('toggle'); }));
  return () => el.replaceChildren();
};

// ---------- sheets ----------

const KIND_EYEBROW = { destructive: 'CONFIRM · CANNOT BE UNDONE', money: 'CONFIRM · MONEY', server: 'CONFIRM · SERVER TO SERVER' } as const;
let sheetSeq = 0;

/** The confirm sheet: Cancel has focus, Enter on Confirm or a 600 ms pointer hold confirms, Esc cancels. */
export const openConfirm = (host: HTMLElement, req: { title: string; body: string; kind: 'destructive' | 'money' | 'server'; confirmLabel: string }): Promise<boolean> =>
  new Promise((resolve) => {
    sheetSeq += 1;
    const titleId = `confirm-title-${sheetSeq}`;
    const bodyId = `confirm-body-${sheetSeq}`;
    const before = document.activeElement as HTMLElement | null;
    const hint = h('p', { class: 'hint', 'aria-live': 'polite' }, 'Hold to confirm with a pointer · Enter from the keyboard · Esc cancels');
    const cancel = h('button', { type: 'button', class: 'ghost' }, 'Cancel');
    const ok = h('button', { type: 'button', class: `hold ${req.kind}` }, h('span', { class: 'hold-fill', 'aria-hidden': 'true' }), h('span', { class: 'lab' }, req.confirmLabel));
    const sheet = h('section', { class: `sheet confirm kind-${req.kind}`, role: 'alertdialog', 'aria-modal': 'true', 'aria-labelledby': titleId, 'aria-describedby': bodyId },
      h('p', { class: 'eyebrow' }, KIND_EYEBROW[req.kind]),
      h('h2', { id: titleId }, req.title),
      h('p', { id: bodyId, class: 'body' }, req.body),
      h('div', { class: 'row' }, cancel, ok),
      hint);
    const scrim = h('div', { class: 'scrim' }, sheet);
    let done = false;
    const offs: Unsub[] = [];
    const finish = (answer: boolean): void => {
      if (done) return;
      done = true;
      for (const off of offs) off();
      scrim.remove();
      before?.focus?.();
      resolve(answer);
    };
    offs.push(trapFocus(sheet));
    offs.push(bindHold(ok, () => finish(true), { onHint: () => { hint.textContent = 'Keep holding: 600 ms confirms. Or press Enter.'; } }));
    cancel.addEventListener('click', () => finish(false));
    scrim.addEventListener('pointerdown', (e) => { if (e.target === scrim) finish(false); });
    sheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); finish(false); } });
    host.append(scrim);
    cancel.focus();
  });

const humanize = (id: string): string => id.replace(/[-_.:]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();

/** A ring entry's words: the verb's plain title ("Claim these slots") when it has one, else its id in words. */
export const ringLabel = (v: Verb): string => {
  const title = v.title;
  if (typeof title === 'string' && title.trim() !== '') return title;
  const words = humanize(v.id);
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** An entry that is not a verb of the hand: dive into a World, open a relay's Loom. */
export interface RingExtra {
  readonly label: string;
  /** What it does, in mono where a verb shows its operationId. */
  readonly hint: string;
  run(): void;
}

export interface RingOptions {
  /** An entry was chosen: the verb and its index in the verbs shown. */
  onPick(verb: Verb, index: number): void;
  /** The ring closed with nothing chosen: Esc, Tab, a click elsewhere, focus moving away. */
  onClose(): void;
  /** Entries after the verbs (before them with `lead`: a relay chip's first entry opens its Loom). */
  readonly extras?: readonly RingExtra[];
  readonly lead?: boolean;
}

let closeOpenRing: (() => void) | null = null;

/**
 * The verb ring: every verb where the subject is the source or the target,
 * in plain words, each ending in its operationId, then any extras. Arrows,
 * Home and End move; Enter chooses; Esc and Tab close. The returned function
 * closes it without calling `onClose` (the hand already left its ring).
 */
export const showRing = (host: HTMLElement, verbs: readonly Verb[], opts: RingOptions): (() => void) => {
  closeOpenRing?.();
  const before = document.activeElement as HTMLElement | null;
  // What undoes (a drop on the Void: release, withdraw, revoke) comes last, so the entry Enter lands on first never does.
  const verbEntries = verbs.map((v, i) => ({ label: ringLabel(v), mono: v.op, flag: v.confirm, pick: () => opts.onPick(v, i), undoes: v.target === 'gate:void' }));
  const ordered = [...verbEntries.filter((e) => !e.undoes), ...verbEntries.filter((e) => e.undoes)];
  const extraEntries = (opts.extras ?? []).map((x) => ({ label: x.label, mono: x.hint, flag: null, pick: () => x.run(), undoes: false }));
  const entries = opts.lead ? [...extraEntries, ...ordered] : [...ordered.filter((e) => !e.undoes), ...extraEntries, ...ordered.filter((e) => e.undoes)];
  const items = entries.map((e) => h('button', { type: 'button', role: 'menuitem', class: 'ring-item', tabindex: '-1' },
    h('span', { class: 'lab' }, e.label), h('code', {}, e.mono), e.flag ? h('span', { class: 'flag' }, e.flag) : null));
  const menu = h('div', { class: 'ring-menu', role: 'menu', 'aria-label': 'Verbs' },
    items.length > 0 ? items : [h('p', { class: 'hint' }, 'Nothing can be done with this here.')]);
  const at = before && before !== document.body ? centerOf(before) : null;
  if (at) {
    menu.style.setProperty('--vx', `${Math.round(at.x)}px`);
    menu.style.setProperty('--vy', `${Math.round(at.y)}px`);
    menu.classList.add('at');
  }
  let index = 0;
  const focusAt = (i: number): void => {
    if (items.length === 0) return;
    index = (i + items.length) % items.length;
    items.forEach((b, j) => b.setAttribute('tabindex', j === index ? '0' : '-1'));
    items[index]!.focus();
  };
  let done = false;
  /** Take the menu away; true the first time. With `restore`, focus goes back to the ring's subject. */
  const finish = (restore: boolean): boolean => {
    if (done) return false;
    done = true;
    document.removeEventListener('pointerdown', outside, true);
    if (closeOpenRing === silent) closeOpenRing = null;
    menu.remove();
    if (restore && before?.isConnected) before.focus();
    return true;
  };
  const cancel = (restore: boolean): void => { if (finish(restore)) opts.onClose(); };
  const silent = (): void => { finish(menu.contains(document.activeElement)); };
  const outside = (e: Event): void => { if (!menu.contains(e.target as Node)) cancel(false); };
  items.forEach((b, i) => b.addEventListener('click', () => { if (finish(true)) entries[i]!.pick(); }));
  menu.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); focusAt(index + 1); }
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); focusAt(index - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
    else if (e.key === 'End') { e.preventDefault(); focusAt(items.length - 1); }
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); cancel(true); }
  });
  // F6, or anything else that takes focus away, closes it where focus went.
  menu.addEventListener('focusout', (e) => {
    const to = e.relatedTarget as Node | null;
    if (to && menu.contains(to)) return;
    if (to) cancel(false);
  });
  host.append(menu);
  document.addEventListener('pointerdown', outside, true);
  closeOpenRing = silent;
  if (items.length > 0) focusAt(0);
  else { menu.setAttribute('tabindex', '-1'); menu.focus(); }
  return silent;
};

export interface CapsuleOptions {
  readonly title: string;
  readonly secret: string;
  readonly note: string;
  /** Things to do with the secret; each closes the capsule. */
  readonly adopt?: readonly { label: string; run: (secret: string) => void }[];
}

/**
 * A secret shown once (§8 rule 2): it leaves the DOM when it is copied, used
 * or closed, and nothing else keeps it.
 */
export const showCapsule = (host: HTMLElement, o: CapsuleOptions): void => {
  const before = document.activeElement as HTMLElement | null;
  let secret: string | null = o.secret;
  const code = h('code', { class: 'secret' }, o.secret);
  const status = h('p', { class: 'hint', 'aria-live': 'polite' }, 'Shown once: it disappears when you copy it, use it, or close this.');
  const copy = h('button', { type: 'button', class: 'primary' }, 'Copy');
  const close = h('button', { type: 'button', class: 'ghost' }, 'Close');
  const adopts = (o.adopt ?? []).map((a) => {
    const b = h('button', { type: 'button' }, a.label);
    b.addEventListener('click', () => { const s = secret; gone(); if (s) a.run(s); });
    return b;
  });
  const el = h('section', { class: 'sheet capsule-secret', role: 'dialog', 'aria-modal': 'true', 'aria-label': o.title },
    h('p', { class: 'eyebrow' }, 'SHOWN ONCE'),
    h('h2', {}, o.title),
    h('p', { class: 'body' }, o.note),
    code,
    h('div', { class: 'row' }, copy, ...adopts, close),
    status);
  const scrim = h('div', { class: 'scrim' }, el);
  const offTrap = trapFocus(el);
  const gone = (): void => {
    secret = null;
    code.textContent = '';
    offTrap();
    scrim.remove();
    before?.focus?.();
  };
  copy.addEventListener('click', async () => {
    const s = secret;
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s);
      gone();
    } catch {
      status.textContent = 'The clipboard said no: select the secret and copy it by hand, then close.';
      const range = document.createRange();
      range.selectNodeContents(code);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  });
  close.addEventListener('click', gone);
  el.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); gone(); } });
  host.append(scrim);
  copy.focus();
};

/** The nouns of the playground, one sentence each: what the '?' sheet says. */
export const GLOSSARY: readonly (readonly [string, string])[] = [
  ['World', 'One resource\'s calendar: a room, a doctor, an oven. Its hours (or, on a minute World, its minutes) are bits.'],
  ['Bit', 'One hour of a day, or one minute of an hour. Lit when someone holds it; dark when it is free.'],
  ['Occupier', 'Who or what may hold time in a World: a holder (a person), or an event or a series in a holder\'s name.'],
  ['Claim', 'Asking a World\'s door for some bits. The door answers with a receipt or a reason, never both, never silence.'],
  ['Receipt', 'The door\'s yes: the commit\'s number and hash in the World\'s journal.'],
  ['Refusal', 'The door\'s no, with who holds the time and what to do next: queue, take the next free run, or ask why.'],
  ['Seat', 'How many holders one bit takes at once (a World\'s capacity). One seat: first come, first served.'],
  ['Soft hold', 'A hold that reserves nothing: it waits its turn and becomes firm when confirmed.'],
  ['Journal · River', 'Every change to a World, chained by hash. Drag the River\'s playhead back to see the World as it was.'],
  ['Keeper', 'The World itself as an occupier: holds a planner makes for its relays are in its name.'],
  ['Relay', 'One job carried through several Worlds, one leg each: dough, then oven, then bike.'],
  ['Hand', 'A World a planner has taken in for its relays. Another workspace\'s hand is a copy: when it is busy, not by whom.'],
  ['Ask', 'A relay\'s request for a leg\'s time on a hand. Its owner accepts, says no, or offers another time.'],
  ['Grant', 'Letting another workspace see your Worlds, or ask them for time.'],
  ['Director', 'Claude working the stage through the API\'s own MCP tools, with your key. Every call it makes is on the Wire.'],
  ['Cases', 'Use cases the stage plays with no AI: a ghost hand performs each step, and every call is real.'],
];

let closeGlossary: (() => void) | null = null;

/** The '?' sheet: what each noun means, and where to begin. Not modal: Esc, × or '?' again put it away. */
export const showGlossary = (host: HTMLElement, go: (to: Route) => void): void => {
  if (closeGlossary) { closeGlossary(); return; }
  const before = document.activeElement as HTMLElement | null;
  const x = h('button', { type: 'button', class: 'panel-x', 'aria-label': 'Close (Esc)' }, '×');
  const cases = h('button', { type: 'button', class: 'primary small' }, '▶ Watch a case play');
  const el = h('section', { class: 'sheet glossary', role: 'dialog', 'aria-label': 'What the words mean' },
    x,
    h('p', { class: 'eyebrow' }, 'WHAT THE WORDS MEAN'),
    h('h2', {}, 'An API that books time on shared things, and always answers.'),
    h('p', { class: 'body' }, 'Carry a thing from the rail on the left and drop it where it goes: a seed on the stage makes a World, a person on some hours books them, the Lens on anything reads it. Every drop is one real API call, shown on the Wire.'),
    h('dl', { class: 'glossary-list' }, ...GLOSSARY.flatMap(([term, means]) => [h('dt', {}, term), h('dd', {}, means)])),
    h('div', { class: 'row' }, cases));
  const off = trapFocus(el);
  const close = (): void => {
    off();
    el.remove();
    closeGlossary = null;
    if (before?.isConnected) before.focus();
  };
  closeGlossary = close;
  x.addEventListener('click', close);
  cases.addEventListener('click', () => { close(); go({ name: 'gallery' }); });
  el.addEventListener('keydown', (e) => { if (e.key === 'Escape' || e.key === '?') { e.preventDefault(); e.stopPropagation(); close(); } });
  host.append(el);
  x.focus();
};

/** A short line that outlives a screen change: why the tab is back on Connect, a rotation, a saved recording. */
export const showNotice = (host: HTMLElement, line: string, tone: 'ok' | 'warn' | 'refuse'): void => {
  const el = h('div', { class: `toast ${tone}`, role: 'status' }, line,
    h('button', { type: 'button', class: 'x', 'aria-label': 'Dismiss', onclick: () => el.remove() }, '×'));
  host.append(el);
  setTimeout(() => el.remove(), 9_000);
};

// ---------- the carried ghost ----------

interface Ghost { show(held: Held | null, at?: { x: number; y: number }, home?: boolean): void }

/**
 * The ghost of what is carried, on the Fx's springs (§4.3): it follows at
 * k 320 / c 26, and a refused drop snaps home at k 520 / c 30; under reduced
 * motion it follows directly and the snap is instant.
 */
const createGhost = (host: HTMLElement, fx: Fx): Ghost => {
  const el = h('div', { class: 'carry-ghost', 'aria-hidden': 'true', hidden: true });
  host.append(el);
  const spring: FxGhost = fx.ghost(el);
  let current: Held | null = null;
  let last: { x: number; y: number } | null = null;
  let home: { x: number; y: number } | null = null;
  fx.origins.register('ghost', () => (current ? last : null));
  const hide = (): void => {
    current = null;
    home = null;
    el.hidden = true;
  };
  return {
    show(held, at, goHome) {
      if (!held) { hide(); return; }
      if (held !== current && held.id !== current?.id) {
        current = held;
        // A case's ghost hand is a hand, not a chip: its own cursor, in the scenario's amber-white (§4.4).
        const scenario = isObj(held.data) && held.data.ghost === true;
        el.classList.toggle('is-scenario', scenario);
        mount(el, scenario ? h('span', { class: 'hand', 'aria-hidden': 'true' }, '☝') : null, h('span', { class: 'lab' }, held.label), h('code', {}, held.kind));
        if (at) { spring.jump(at.x, at.y); last = { ...at }; home = { ...at }; }
        el.hidden = false;
      }
      if (goHome) {
        const back = home ?? last;
        if (back) spring.home(back.x, back.y, hide);
        else hide();
      } else if (at) {
        last = { ...at };
        spring.to(at.x, at.y);
      }
    },
  };
};
